import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { AlertTriangle, Check, CheckCircle2, Loader2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { REPORT_STATUS_CLASSES } from '../../components/crags/report-status';
import { cragReportsService } from '../../services/crag-reports';
import { useCurrentCrag } from '../hooks/useCurrentCrag';
import type { CragReport, CragReportStatus } from '../../types/crag-report';

/** The three lists, in the order the queue is worked through. */
const TABS: { value: CragReportStatus; icon: typeof AlertTriangle }[] = [
  { value: 'draft', icon: AlertTriangle },
  { value: 'accepted', icon: Check },
  { value: 'resolved', icon: CheckCircle2 },
];

/**
 * The crag's report queue: what visitors have filed, what has been vouched for,
 * and what has been fixed.
 *
 * Accepting is the consequential button on this page and it says so — it is
 * what forwards the report by email to every maintainer of the crag, in the
 * crag's name. Rejecting is silent, and exists so a queue can actually be
 * emptied rather than only ever grown.
 */
export default function ReportsPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { crag, loading: cragLoading, slug } = useCurrentCrag();
  const dateLocale = i18n.language === 'it' ? it : enUS;

  const [reports, setReports] = useState<CragReport[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);
  // The report being closed, and the note that closes it.
  const [resolving, setResolving] = useState<CragReport | null>(null);
  const [resolutionMessage, setResolutionMessage] = useState('');

  const loadList = useCallback(
    async (cragSlug: string) => {
      setListLoading(true);
      try {
        const { reports: fetched } = await cragReportsService.list(cragSlug);
        setReports(fetched);
      } catch (error) {
        console.error('Failed to load reports:', error);
        toast({ title: t('gymAdmin.reports.loadError'), variant: 'destructive' });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug) loadList(slug);
  }, [slug, loadList]);

  const byStatus = useMemo(() => {
    const groups: Record<CragReportStatus, CragReport[]> = {
      draft: [],
      accepted: [],
      resolved: [],
      rejected: [],
    };
    for (const report of reports) groups[report.status]?.push(report);
    return groups;
  }, [reports]);

  /** Swaps the changed report into the list in place — no reload, no jump. */
  const replaceReport = (updated: CragReport) => {
    setReports((prev) => prev.map((report) => (report.id === updated.id ? updated : report)));
  };

  const runTransition = async (
    report: CragReport,
    action: () => Promise<CragReport>,
    successKey: string
  ) => {
    setPendingId(report.id);
    try {
      replaceReport(await action());
      toast({ title: t(successKey) });
    } catch (error) {
      toast({
        title: t('gymAdmin.reports.actionError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setPendingId(null);
    }
  };

  const handleAccept = (report: CragReport) => {
    if (!slug) return;
    runTransition(
      report,
      () => cragReportsService.accept(slug, report.id),
      'gymAdmin.reports.accepted'
    );
  };

  const handleReject = (report: CragReport) => {
    if (!slug) return;
    if (!window.confirm(t('gymAdmin.reports.confirmReject'))) return;
    setPendingId(report.id);
    cragReportsService
      .reject(slug, report.id)
      .then(() => {
        // A rejected report leaves every list — it is not shown to anyone.
        setReports((prev) => prev.filter((item) => item.id !== report.id));
        toast({ title: t('gymAdmin.reports.rejected') });
      })
      .catch((error: unknown) => {
        toast({
          title: t('gymAdmin.reports.actionError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      })
      .finally(() => setPendingId(null));
  };

  const handleResolve = async () => {
    if (!slug || !resolving) return;
    await runTransition(
      resolving,
      () => cragReportsService.resolve(slug, resolving.id, resolutionMessage.trim() || undefined),
      'gymAdmin.reports.resolved'
    );
    setResolving(null);
    setResolutionMessage('');
  };

  if (cragLoading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  // The menu entry is crag-only, so a gym reaching this page is a stale URL.
  if (!crag || !slug) return null;

  const renderReport = (report: CragReport) => {
    const busy = pendingId === report.id;

    return (
      <Card key={report.id}>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={REPORT_STATUS_CLASSES[report.status]}>
              {t(`cragReports.status.${report.status}`)}
            </Badge>
            <Badge variant="outline">{t(`cragReports.categories.${report.category}`)}</Badge>
            <span className="text-sm font-medium">
              {report.climb ? report.climb.name : t('gymAdmin.reports.wholeCrag')}
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              {format(new Date(report.created_at), 'd MMM yyyy', { locale: dateLocale })}
            </span>
          </div>

          <p className="whitespace-pre-wrap text-sm">{report.description}</p>

          {report.reported_by && (
            <p className="text-xs text-muted-foreground">
              {t('gymAdmin.reports.reportedBy', { username: report.reported_by.username })}
            </p>
          )}

          {report.resolution_message && (
            <div className="rounded-md bg-green-500/10 p-2 text-sm text-green-800 dark:text-green-300">
              {report.resolution_message}
            </div>
          )}

          {report.status === 'draft' && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy} onClick={() => handleAccept(report)}>
                {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Check className="mr-2 size-4" />}
                {t('gymAdmin.reports.accept')}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => handleReject(report)}>
                <X className="mr-2 size-4" />
                {t('gymAdmin.reports.reject')}
              </Button>
              <p className="w-full text-xs text-muted-foreground">{t('gymAdmin.reports.acceptHint')}</p>
            </div>
          )}

          {report.status === 'accepted' && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setResolving(report);
                setResolutionMessage('');
              }}
            >
              <CheckCircle2 className="mr-2 size-4" />
              {t('gymAdmin.reports.resolve')}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="max-w-4xl space-y-6">
      <p className="text-sm text-muted-foreground">{t('gymAdmin.reports.subtitle')}</p>

      {listLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <Tabs defaultValue="draft">
          <TabsList className="grid w-full grid-cols-3">
            {TABS.map(({ value, icon: Icon }) => (
              <TabsTrigger key={value} value={value} className="gap-1.5">
                <Icon className="size-4" />
                {t(`gymAdmin.reports.tabs.${value}`)}
                <span className="text-xs text-muted-foreground">({byStatus[value].length})</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map(({ value }) => (
            <TabsContent key={value} value={value} className="space-y-3">
              {byStatus[value].length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-muted-foreground">
                    {t(`gymAdmin.reports.empty.${value}`)}
                  </CardContent>
                </Card>
              ) : (
                byStatus[value].map(renderReport)
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={!!resolving} onOpenChange={(open) => { if (!open) setResolving(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('gymAdmin.reports.resolveTitle')}</DialogTitle>
            <DialogDescription>{t('gymAdmin.reports.resolveDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="resolution-message">{t('gymAdmin.reports.resolutionLabel')}</Label>
            <Textarea
              id="resolution-message"
              value={resolutionMessage}
              onChange={(event) => setResolutionMessage(event.target.value)}
              placeholder={t('gymAdmin.reports.resolutionPlaceholder')}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResolving(null)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleResolve} disabled={!!pendingId}>
              {pendingId && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('gymAdmin.reports.resolve')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
